import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button } from '~/components/ui';
import { useFulfillRequestForQuote } from '~/api';
import { extractGraphQLErrorMessages, type ClientError } from '~/utils';
import type { GetRequestForQuoteWithQuotesQuery } from '~/graphql/graphql';
import {
  isRequestForQuoteFulfillable,
  type FulfillableQuote,
  type FulfillableQuoteItem,
} from './quoteAwarding';

type FulfillRequestForQuoteButtonProps<Item extends FulfillableQuoteItem> = {
  requestForQuote: Pick<
    NonNullable<GetRequestForQuoteWithQuotesQuery['requestForQuote']>,
    'id' | 'status' | 'items'
  >;
  quotes: ReadonlyArray<FulfillableQuote<Item>>;
};

export function FulfillRequestForQuoteButton<
  Item extends FulfillableQuoteItem,
>({
  requestForQuote,
  quotes,
}: Readonly<FulfillRequestForQuoteButtonProps<Item>>) {
  const { t } = useTranslation('rfq-view');
  const { mutateAsync: fulfillRequestForQuote, isPending } =
    useFulfillRequestForQuote();

  if (!isRequestForQuoteFulfillable(requestForQuote, quotes)) {
    return null;
  }

  const handleFulfill = async () => {
    try {
      await fulfillRequestForQuote({ id: requestForQuote.id });
      toast.success(t('rfq_view.rfq_fulfilled'));
    } catch (error) {
      toast.error(extractGraphQLErrorMessages(error as ClientError).join(', '));
    }
  };

  return (
    <Button
      variant="secondary"
      size="sm"
      width="auto"
      onClick={handleFulfill}
      disabled={isPending}
      loading={isPending}
    >
      {t('rfq_view.mark_as_fulfilled')}
    </Button>
  );
}
